import { useCallback } from 'react';
import { connectorDisplayName, fetchConnectors, type ConnectorsStatus, type ConnectorStatus } from './connectors';
import { queryClient, queryKeys } from './query-client';
import { useConnectConnector } from './use-connect-connector';
import { useOpenSideChat } from './use-open-side-chat';

const cachedConnectors = (): ConnectorStatus[] => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [];
const refreshConnectors = () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors });

/** The catalog's name for a toolkit, when the app has already read the connectors list; else undefined. */
export function cachedToolkitName(toolkit: string): string | undefined {
  const row = cachedConnectors().find((c) => c.toolkit === toolkit);
  return row ? connectorDisplayName(row) : undefined;
}

/**
 * Connect ONE toolkit by its slug, from a surface that is not the Connectors screen — the plan review's
 * "Connect Instagram and approve" and a hand-back question's Connect option. It runs the Connectors screen's own
 * dispatch (`useConnectConnector`: Composio popup + poll, native calendar, assistant chat), never a second copy.
 *
 * The row is resolved from the shared connectors cache, read once if the cache is empty, and falls back to a bare
 * Composio row for a toolkit the list does not carry (the dispatch's default rail).
 *
 * Mount it only where a connect can actually be pressed: the dispatch owns the popup and its poll and closes both on
 * unmount, so the component holding it must outlive the OAuth window.
 */
export function useToolkitConnect() {
  const openSideChat = useOpenSideChat();
  const { busy, actionError, actionUpgrade, connect } = useConnectConnector({
    getConnectors: cachedConnectors,
    getGmailScopesEnabled: () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.gmailScopesEnabled ?? false,
    refresh: refreshConnectors,
    openChat: openSideChat,
  });
  const connectToolkit = useCallback(
    async (toolkit: string) => {
      let row = cachedConnectors().find((c) => c.toolkit === toolkit);
      if (!row) {
        try {
          const status = await queryClient.fetchQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
          row = status.connectors.find((c) => c.toolkit === toolkit);
        } catch {
          // The list could not be read; the Composio rail below still connects by slug.
        }
      }
      connect(row ?? { toolkit, connected: false });
    },
    [connect],
  );
  return { busy, actionError, actionUpgrade, connectToolkit };
}
